// Referral Database access — routed through the backend proxy.
// The legacy DB secret NO LONGER lives in the frontend bundle; it is held
// server-side (server/.env -> REFERRAL_DB_SECRET) and attached by the proxy.
// The browser only ever sends a path + optional query filters.

import { authHeaders } from '../../core/api/authHeaders';

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
const referralEndpoint = (path) => `${PROXY_BASE}/api/referral/${String(path).replace(/^\//, '')}`;

// Build query string from REST filter params ({ orderBy, equalTo, shallow })
const buildParams = (params = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) search.set(k, v);
    });
    const s = search.toString();
    return s ? `?${s}` : '';
};

// For API compatibility with the previous SDK-shaped helpers
export const referralRef = (path) => path;

export const referralUpdate = async (path, data) => {
    try {
        const response = await fetch(referralEndpoint(path), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    } catch (error) {
        console.error("[ReferralDB] Update Error:", error);
        throw error;
    }
};

export const referralGet = async (pathOrQuery) => {
    let path, params;
    if (typeof pathOrQuery === 'string') {
        path = pathOrQuery;
        params = {};
    } else {
        path = pathOrQuery.path;
        params = pathOrQuery.params || {};
    }

    try {
        const response = await fetch(referralEndpoint(path) + buildParams(params), { headers: { ...(await authHeaders()) } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        // Return a snapshot-like object for compatibility with existing callers
        return {
            exists: () => data !== null,
            val: () => data,
            numChildren: () => (data && typeof data === 'object') ? Object.keys(data).length : 0
        };
    } catch (error) {
        console.error("[ReferralDB] Get Error:", error);
        throw error;
    }
};

// Query builders for REST (unchanged shape)
export const query = (path, ...filters) => {
    const params = {};
    filters.forEach(f => {
        if (f.type === 'shallow') params.shallow = 'true';
        if (f.type === 'orderBy') params.orderBy = `"${f.field}"`;
        if (f.type === 'equalTo') params.equalTo = typeof f.value === 'string' ? `"${f.value}"` : f.value;
    });
    return { path, params };
};

export const orderByChild = (field) => ({ type: 'orderBy', field });
export const equalTo = (value) => ({ type: 'equalTo', value });

// Real-time listener.
// EventSource/SSE required the secret in the URL (browser-exposed), so it is
// replaced with secure polling through the proxy. Returns an unsubscribe fn.
const REFERRAL_POLL_MS = 15000;
export const referralOnValue = (pathOrQuery, callback) => {
    let stopped = false;
    let timer = null;

    const tick = async () => {
        if (stopped) return;
        try {
            const snap = await referralGet(pathOrQuery);
            if (!stopped) callback(snap);
        } catch (e) {
            // swallow; will retry on next tick
        } finally {
            if (!stopped) timer = setTimeout(tick, REFERRAL_POLL_MS);
        }
    };

    tick(); // immediate first read

    return () => {
        stopped = true;
        if (timer) clearTimeout(timer);
    };
};
